import React, { useEffect } from 'react';
import { Shield } from 'lucide-react';
import '../styles/PrivacyPolicy.css';

const PrivacyPolicyPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="privacy-policy-page">
      <section className="privacy-hero">
        <div className="container">
          <Shield className="hero-icon" />
          <h1>Política de Privacidad y Protección de Datos Personales</h1>
          <p className="subtitle">
            <strong>UNILEARN SpA</strong> · RUT <strong>77.291.711-2</strong>
          </p>
          <p className="subtitle">
            Última actualización: <strong>{new Date().toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric' })}</strong>
          </p>
        </div>
      </section>

      <div className="privacy-content">
        <div className="container">
          <div className="privacy-intro">
            <p>
              Para solicitar <strong>acceso</strong>, <strong>rectificación</strong>, <strong>eliminación</strong> u otros derechos asociados a tus datos personales,
              escríbenos a: <a href="mailto:eliminardato@unilearn.cl">eliminardato@unilearn.cl</a>.
            </p>
            <p className="muted">
              Responderemos en un plazo razonable, de acuerdo con la normativa aplicable y la naturaleza de la solicitud.
            </p>
          </div>

          <div className="privacy-sections">
            <section className="privacy-section">
              <div className="section-header">
                <h2>1. Identificación del responsable del tratamiento</h2>
              </div>
              <div className="section-content">
                <p>
                  <strong>UNILEARN SpA</strong>, RUT <strong>77.291.711-2</strong> (en adelante, "UNILEARN") es responsable del tratamiento de los datos personales que recopila
                  a través de sus sitios web, plataformas digitales, formularios, servicios educativos, canales de comunicación y herramientas tecnológicas asociadas.
                </p>
                <ul>
                  <li>Correo de contacto general: <a href="mailto:contacto@unilearn.cl">contacto@unilearn.cl</a></li>
                  <li>Correo para solicitudes de eliminación de datos: <a href="mailto:eliminardato@unilearn.cl">eliminardato@unilearn.cl</a></li>
                  <li>Sitio web: <a href="https://unilearn.cl/" target="_blank" rel="noopener noreferrer">https://unilearn.cl/</a></li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>2. Datos personales que recopilamos</h2>
              </div>
              <div className="section-content">
                <p>UNILEARN puede recopilar y tratar los siguientes datos personales, según el servicio utilizado:</p>
                <ul>
                  <li>Nombre y apellido.</li>
                  <li>Datos de identificación (por ejemplo, RUT u otro identificador válido, cuando sea necesario).</li>
                  <li>Correo electrónico y número de teléfono.</li>
                  <li>Información académica y/o profesional relevante para los programas ofrecidos.</li>
                  <li>Registros de interacción con nuestros canales (por ejemplo, comunicaciones por correo electrónico o WhatsApp).</li>
                  <li>Información técnica básica: dirección IP, identificadores del dispositivo/navegador, registros de acceso y eventos de seguridad.</li>
                </ul>
                <p className="muted">
                  UNILEARN no solicita ni trata <strong>datos sensibles</strong> salvo que sea estrictamente necesario para la prestación de un servicio específico y siempre
                  bajo las salvaguardas y autorizaciones que correspondan.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>3. Finalidades del tratamiento</h2>
              </div>
              <div className="section-content">
                <p>Los datos personales se tratan para las siguientes finalidades:</p>
                <ul>
                  <li>Gestionar procesos de inscripción, matrícula, participación y certificación en programas educativos.</li>
                  <li>Entregar información académica, administrativa y operativa relacionada con nuestros servicios.</li>
                  <li>Gestionar comunicaciones por canales digitales (incluyendo correo electrónico y/o WhatsApp), por ejemplo: recordatorios, confirmaciones, soporte y avisos.</li>
                  <li>Atender consultas, solicitudes y requerimientos de soporte.</li>
                  <li>Mejorar la calidad de nuestros servicios, contenidos y plataformas (analítica y mejora continua).</li>
                  <li>Cumplir obligaciones legales, regulatorias o contractuales aplicables.</li>
                  <li>En caso de que exista consentimiento, enviar comunicaciones informativas o comerciales sobre cursos, eventos o servicios de UNILEARN.</li>
                </ul>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>4. Base de legitimación</h2>
              </div>
              <div className="section-content">
                <p>
                  UNILEARN trata datos personales cuando existe una base que lo permita, tales como:
                  (i) el <strong>consentimiento</strong> del titular, (ii) la <strong>ejecución de un contrato</strong> o medidas precontractuales,
                  (iii) el <strong>cumplimiento de obligaciones legales</strong>, o (iv) el <strong>interés legítimo</strong> de UNILEARN, cuando corresponda,
                  por ejemplo para fines de seguridad, prevención de fraude y mejora del servicio.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>5. Comunicaciones por WhatsApp y otros canales</h2>
              </div>
              <div className="section-content">
                <p>
                  UNILEARN puede utilizar WhatsApp u otros canales digitales para comunicarse contigo, según la información que nos entregues y tus preferencias.
                  Estas comunicaciones pueden incluir confirmaciones, recordatorios, soporte y mensajes informativos.
                </p>
                <p className="muted">
                  Importante: los proveedores de mensajería pueden aplicar sus propias condiciones y políticas. Recomendamos revisar las políticas del servicio de mensajería
                  que utilices.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>6. Destinatarios, encargados de tratamiento y transferencias</h2>
              </div>
              <div className="section-content">
                <p>
                  UNILEARN puede apoyarse en proveedores que actúan como <strong>encargados del tratamiento</strong> (por ejemplo, servicios de hosting, correo transaccional,
                  analítica, CRM o mensajería) para operar sus servicios. Estos proveedores sólo tratan los datos bajo instrucciones de UNILEARN y con medidas de seguridad razonables.
                </p>
                <p>
                  En caso de transferencias internacionales de datos (por ejemplo, por uso de infraestructura en la nube), UNILEARN procurará que existan salvaguardas adecuadas
                  conforme a la normativa aplicable.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>7. Plazo de conservación</h2>
              </div>
              <div className="section-content">
                <p>
                  Conservamos los datos personales sólo durante el tiempo necesario para cumplir las finalidades descritas en esta política y para cumplir obligaciones legales o
                  contractuales. Luego, los eliminamos o anonimizamos, según corresponda.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>8. Derechos de las personas (titulares de datos)</h2>
              </div>
              <div className="section-content">
                <p>
                  Puedes solicitar información o ejercer derechos asociados a tus datos personales (por ejemplo, acceso, rectificación, actualización, eliminación y/u oposición,
                  según aplique) escribiendo a: <a href="mailto:eliminardato@unilearn.cl">eliminardato@unilearn.cl</a>.
                </p>
                <p className="muted">
                  Para proteger tu seguridad, podemos pedirte información adicional para verificar tu identidad antes de responder una solicitud.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>9. Medidas de seguridad</h2>
              </div>
              <div className="section-content">
                <p>
                  UNILEARN aplica medidas técnicas y organizacionales razonables para proteger los datos personales contra acceso no autorizado, pérdida, alteración o divulgación
                  indebida. Ningún sistema es 100% seguro, pero trabajamos para mantener controles acordes al riesgo.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>10. Cookies y tecnologías similares</h2>
              </div>
              <div className="section-content">
                <p>
                  Nuestros sitios pueden utilizar cookies y tecnologías similares para habilitar funcionalidades, mejorar la experiencia y obtener métricas de uso.
                  Puedes configurar tu navegador para bloquear o eliminar cookies; sin embargo, algunas funciones podrían verse afectadas.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>11. Cambios a esta política</h2>
              </div>
              <div className="section-content">
                <p>
                  UNILEARN puede actualizar esta política cuando sea necesario. Publicaremos la versión vigente en este mismo lugar, indicando la fecha de última actualización.
                </p>
              </div>
            </section>

            <section className="privacy-section">
              <div className="section-header">
                <h2>12. Contacto</h2>
              </div>
              <div className="section-content">
                <p>
                  Si tienes dudas sobre esta política o sobre el tratamiento de tus datos, escríbenos a:
                  <a href="mailto:contacto@unilearn.cl"> contacto@unilearn.cl</a>.
                </p>
              </div>
            </section>

            <div className="privacy-note">
              <p className="muted">
                Nota: Este documento es un texto estándar de política de privacidad para un negocio educativo. Si UNILEARN realiza tratamientos especiales (por ejemplo, pagos,
                facturación electrónica, grabaciones, biometría, datos sensibles, etc.), conviene complementar con secciones específicas.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
